import lodash from 'lodash';
const { isEqual } = lodash;

/**
 * Merge an (optional) location onto an order and decide whether the result should mark the edit
 * form dirty (enabling the Start button). `location` is optional -- a grid click may carry no
 * locationId at all. Status decides whether the merge happens at all, not just whether an
 * unchanged merge counts as dirty:
 * - New/Suspended (start-eligible): always dirty, regardless of whether location changed. A status
 *   with no title at all counts as New here, mirroring the server (#2026).
 * - In Progress: dirty only if the location actually changed (a location correction is save-able).
 * - anything else (Closed*, Cancelled, ...): never dirty -- undefined unconditionally, so a
 *   closed order's location is never rewritten just because it was opened under a location filter.
 * @param {object} order
 * @param {object} [location]
 * @returns {{ activeRecord: object, oldRecord: object } | undefined}
 */
export function getLocationMerge(order, location) {
    // A title-less `order:status` means 'New', the same fallback the server applies (#2026).
    // Without it the regex below tested the literal string "undefined", no merge happened, the
    // edit form never went dirty, and the Start button never enabled — a second, independent
    // block on starting such an order. Only a status with no title of its own reaches this
    // fallback, so Closed/Cancelled still returns undefined below.
    const status = order['order:status']?.title || 'New';
    if (!/new|suspended|in progress/i.test(status)) {
        return undefined;
    }

    const activeRecord = location ? { ...order, ...getLocationInfo(location) } : { ...order };
    if (/in progress/i.test(status) && isEqual(order, activeRecord)) {
        return undefined;
    }
    return { activeRecord, oldRecord: order };
}

// NOTE: if the new location doesn't carry a location:building/location:company tier, the order's
// existing one (from wherever it was previously) is left in place rather than cleared. This is a
// pre-existing characteristic of this merge (originally only reachable via Scan Order Number);
// this module doesn't change that behavior. See PR review on #1792 / incident #1642
// (location-hierarchy corruption) before broadening how this gets used.
export function getLocationInfo(location) {
    const info = {
        'location:location': { _id: location._id, title: location.title }
    };
    if (location['location:building']) {
        info['location:building'] = location['location:building'];
    }
    if (location['location:company']) {
        info['location:company'] = location['location:company'];
    }
    return info;
}
